/**
 * kumo.news — サービス（Kumo）自身のお知らせの同意の欄（Kumo が描く部品）。2026-09-27 founder の決定 + 管理判断
 * （docs/research/2026-09-27-mail-kumo-optin.md K1〜K13）。サーバーは apps/server/src/modules/mailconsent/newsRoutes.ts。
 *
 *   // メールアドレスの登録画面（ゲームのお知らせのチェックボックスの下など）に置く
 *   const news = await kumo.news.mountNewsOptIn(document.querySelector('#kumo-news'));
 *   // 登録が成功した後に（チェックされていなければ何もしない）
 *   await kumo.auth.signUpWithEmail(email, password);
 *   await news.submit(email);
 *
 * WHY Kumo が描く部品か（signUpWithEmail の引数に足すだけにしないか）: 文言（送り主の名指し・「このゲームを動かしているサービス」・16 歳以上・
 *   いつでも停止）と初期状態（チェックなし）を Kumo が保証し、同意の記録の「文言の版」「部品の版」と画面を結び付けるため（K6・EDPB para 108
 *   「設定が正しかったと言うだけでは足りない」）。クリエイター（の AI）が文言を書くと、抜け・最初からチェック済みを防げない。
 *
 * 決まり（この部品が守る）:
 *   - **初期は常にチェックなし**（K1）。画面を戻っても・出し直しても勝手に入らない（autocomplete="off" = ブラウザの入力の復元もさせない）
 *   - 出すかどうかはサーバーが決める（adult だけ・kids のゲームでは出さない = K4）。出さないときは container に何も描かない
 *   - チェックしなくても登録・プレイは完了する（K3）。submit の失敗は例外にしない（ログインの流れを止めない）
 *   - ロゴ・ポータルへのリンクは出さない（K9。出すのはプライバシーポリシーのリンクだけ）
 *   - 見た目はゲームの文字を受け継ぐ（font: inherit）。ゲームのお知らせのチェックボックスと同じ大きさの 1 行 + 読める大きさの補足
 * 確認メール（K5）: submit の後、本人が確認メールのリンクを押すまで名簿に入らない。停止はメールのリンクか unsubscribe(email?)。
 */

import type { KumoCore } from './core.js';
import { KumoError } from './core.js';

/** この部品の版（同意の記録に残る。見た目・文言の並びを変えたら上げる） */
export const NEWS_WIDGET_VERSION = 'kumo-widget/1';

export interface NewsOptInOptions {
  /** 文言の言語（既定: プレイヤーの locale / ブラウザの言語からサーバーが選ぶ） */
  locale?: 'en' | 'ja';
}

export interface NewsSubscribeResult {
  /** pending = 確認メールを送った（リンクが押されると名簿に入る） */
  state: 'pending';
  /** false = 直前（60 秒以内）に同じアドレスへ送ったので送らなかった */
  sent: boolean;
  expiresAt?: string;
}

export interface NewsOptInHandle {
  /** 欄を出したか（false = 大人でない・kids のゲーム・まだ準備ができていない。container には何も描いていない） */
  readonly shown: boolean;
  /** いまチェックされているか */
  readonly checked: boolean;
  /**
   * 登録が成功した後に呼ぶ。チェックされていなければ何もしない（null）。失敗しても例外にしない（null・console.warn）
   * email: プレイヤーがその画面で入力したアドレス（K8: 名簿のアドレスはチェックの時に本人から直接受け取った値）
   */
  submit(email: string): Promise<NewsSubscribeResult | null>;
  /** 欄を消す（画面を閉じるとき） */
  destroy(): void;
}

/** 部品が触る DOM の部分（テストで差し替える。ブラウザでは本物の要素） */
export interface NewsElementLike {
  ownerDocument: { createElement(tag: string): NewsElementLike } | null;
  appendChild(child: NewsElementLike): unknown;
  removeChild?(child: NewsElementLike): unknown;
  setAttribute(name: string, value: string): void;
  textContent: string | null;
  checked?: boolean;
  href?: string;
  style?: { cssText: string };
  parentNode?: NewsElementLike | null;
}

interface OptInText {
  show: boolean;
  reason?: string;
  version?: string;
  locale?: 'en' | 'ja';
  label?: string;
  detail?: string;
  privacyLink?: string;
  privacyUrl?: string | null;
}

export function createNews(core: KumoCore) {
  const fetchText = (locale?: 'en' | 'ja') =>
    // ログイン前でも取れる（鍵だけ）。サインイン済みなら申告した年齢で判定される
    core.request<OptInText>('GET', `/v1/news/optin${locale ? `?lang=${locale}` : ''}`, undefined, { auth: core.isSignedIn });

  return {
    /**
     * Kumo のお知らせの欄を container に描く。メールアドレスの登録画面に入れる（**既定で出る**・クリエイターはゲームの config の `kumoNews.optIn: false` で外せる = founder 2026-09-27 に「外せない」から改めた。外したゲームでは何も描かない）。
     * 出すかどうか・文言はサーバーが決める（shown が false なら何も描かない）
     */
    async mountNewsOptIn(container: NewsElementLike, opts: NewsOptInOptions = {}): Promise<NewsOptInHandle> {
      let text: OptInText;
      try {
        text = await fetchText(opts.locale);
      } catch (e) {
        // 取れない（オフライン等）= 出さない。登録の画面は止めない
        console.warn('[game-backend] news opt-in unavailable', e instanceof KumoError ? e.code : e);
        text = { show: false, reason: 'unavailable' };
      }
      const doc = container.ownerDocument;
      if (!text.show || !doc) return { shown: false, checked: false, submit: async () => null, destroy: () => {} };

      const root = doc.createElement('div');
      root.setAttribute('data-kumo-news-optin', NEWS_WIDGET_VERSION);
      if (root.style) root.style.cssText = 'font:inherit;margin:8px 0';
      const label = doc.createElement('label');
      if (label.style) label.style.cssText = 'display:flex;gap:.5em;align-items:flex-start;cursor:pointer;font:inherit';
      const box = doc.createElement('input');
      box.setAttribute('type', 'checkbox');
      // ブラウザの「戻る」での入力の復元もさせない（K1: 外したチェックが戻ったら入っている、を作らない）
      box.setAttribute('autocomplete', 'off');
      box.checked = false;
      const span = doc.createElement('span');
      span.textContent = text.label ?? '';
      label.appendChild(box);
      label.appendChild(span);
      const detail = doc.createElement('div');
      // 小さな字でも読める大きさ（ガイドライン: 小さすぎる字・目立たない色は不可）
      if (detail.style) detail.style.cssText = 'font-size:.85em;line-height:1.5;margin:2px 0 0 1.6em;opacity:.85';
      // 補足の文は別の span（出し直しで textContent を替えても、横のプライバシーポリシーのリンクを消さない）
      const detailText = doc.createElement('span');
      detailText.textContent = `${text.detail ?? ''} `;
      detail.appendChild(detailText);
      if (text.privacyUrl) {
        const a = doc.createElement('a');
        a.setAttribute('href', text.privacyUrl);
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
        a.textContent = text.privacyLink ?? '';
        detail.appendChild(a);
      }
      root.appendChild(label);
      root.appendChild(detail);
      container.appendChild(root);

      let version = text.version!;
      let locale = text.locale ?? 'en';
      return {
        shown: true,
        get checked() {
          return !!box.checked;
        },
        async submit(email: string) {
          if (!box.checked) return null;
          try {
            return await core.request<NewsSubscribeResult>('POST', '/v1/news/subscribe', {
              email, textVersion: version, locale, defaultUnchecked: true, method: 'kumo_widget', uiVersion: NEWS_WIDGET_VERSION
            });
          } catch (e) {
            if (e instanceof KumoError && e.code === 'text_outdated') {
              // 表示の後に文言が変わった = 本人が見ていない文言で記録しない。新しい文言で出し直し、チェックも外す（もう一度選んでもらう）
              const fresh = await fetchText(opts.locale).catch(() => null);
              if (fresh?.show) {
                version = fresh.version!;
                locale = fresh.locale ?? locale;
                span.textContent = fresh.label ?? '';
                detailText.textContent = `${fresh.detail ?? ''} `;
              }
              box.checked = false;
            }
            console.warn('[game-backend] news opt-in failed', e instanceof KumoError ? e.code : e);
            return null;
          }
        },
        destroy() {
          container.removeChild?.(root);
        }
      };
    },
    /**
     * Kumo のお知らせを止める（即時）。ゲームのお知らせ（kumo.mail）は止まらない。ログイン中のプレイヤーが要る。
     * 止まるのは**このプレイヤー自身のアドレスだけ**（確認済みのログインのメール、またはこのプレイヤーが部品で登録したアドレス）。
     * email を省くとそれら全部、渡すとそのアドレスだけ（本人のアドレスでなければ何もしない）。
     * 応答は常に { state: 'withdrawn' }（止まったか・名簿にいたかは返さない = 他人のアドレスを調べる手段にさせない。サーバーの SEC-NEWS-04・2026-09-27）。
     * 本人のアドレスでないものは、メールの停止リンクから止める
     */
    unsubscribe: (email?: string) =>
      // email を省いたときは body に入れない（undefined のキーを送らない）
      core.request<{ state: 'withdrawn' }>('POST', '/v1/news/unsubscribe', email === undefined ? {} : { email })
  };
}

export type KumoNews = ReturnType<typeof createNews>;
